import React, { useState, useEffect, useRef } from "react";
import { ArrowCircleRight } from "@mui/icons-material";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  Typography,
  IconButton,
  InputAdornment,
  Box,
  Button,
  Stack,
} from "@mui/material";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import {
  setToken,
  getStoredToken,
  setRememberMeToken,
  removeToken,
  parseJwt,
  isTokenExpired,
} from "../../utils/auth";
import { getPasskeyAssertion } from "../../utils/passkeys/passkeys";
import PasskeyCancelledError from "../../utils/passkeys/passkeyCancelledError";
import {
  isPasskeySupported,
  isConditionalMediationAvailable,
} from "../../utils/passkeys/webauthn";
import {
  signIn,
  getPasskeyAuthenticationOptions,
  authenticateWithPasskey,
  checkPasskey,
} from "../../utils/loginApi";
import LoginTextField from "./LoginTextField";
import OtpInput from "./OtpInput";

const PARTIAL_CONTENT_CODE = 206;
const NUMBER_OF_OTP_DIGITS = 6;

const dangerStyle = { color: "#ff8a80", mt: 1 };

const successBannerStyle = {
  color: "#ffffff",
  textAlign: "center",
  fontSize: "14px",
  mb: 2,
  p: 1.5,
  backgroundColor: "#2e7d32",
  borderRadius: 1,
};

export default function LoginScreen() {
  const { t, i18n } = useTranslation("common", { keyPrefix: "login" });
  const { register, handleSubmit, setValue } = useForm();
  const [searchParams] = useSearchParams();
  const [dbErrors, setDbErrors] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showOtpScreen, setShowOtpScreen] = useState(false);
  const [otpDelivery, setOtpDelivery] = useState("email");
  const [otpCode, setOtpCode] = useState("");
  // Backup codes are only ever an option for app-based 2FA -- email OTP
  // already has no-lockout-risk since it can just be resent.
  const [useBackupCode, setUseBackupCode] = useState(false);
  // True while a passkey ceremony has been forced (preferred_2fa_method is
  // "passkey") -- stays true after a failed/cancelled attempt too, so the
  // "sign in another way" escape hatch below remains available. Losing a
  // passkey device doesn't remove its registration server-side, so without
  // this a user in that state would have no way back in even with a correct
  // password.
  const [passkeyFlowActive, setPasskeyFlowActive] = useState(false);
  const [bypassPasskey, setBypassPasskey] = useState(false);
  const isResetSuccess = searchParams.get("reset_success") === "true";
  const passkeySupported = isPasskeySupported();
  const conditionalAbortRef = useRef(null);

  // Drop a stale submit error the moment the user edits any field.
  const clearError = () => setDbErrors((prev) => (prev ? null : prev));

  const onChange = (value) => {
    setOtpCode(value);
    clearError();
  };

  const handleRedirectLocation = (payload) => {
    try {
      const userType = payload.type;
      let location = searchParams.get("redirect");

      // Only a same-origin relative path may be replayed after login. "//host"
      // and "/\host" are protocol-relative escapes, not paths.
      if (location && !/^\/(?![/\\])/.test(location)) {
        location = null;
      }

      // A v2 redirect target is handed through untouched; the v2 layout resolves
      // its own scope.
      if (location && location.includes("/v2/")) return location;

      // Drop a redirect the current role can't reach.
      if (
        (userType === "guardian" && !location?.includes("guardian")) ||
        (userType === "student" && !location?.includes("student")) ||
        (userType === "staff" && !location?.includes("school"))
      ) {
        location = null;
      }

      // Default landing per role.
      if (!location) {
        if (userType === "staff" || userType === "administrator") {
          location = `/v2/school/${payload.default_school.slug}`;
        } else if (userType === "guardian") {
          location = "/guardian";
        } else if (userType === "student") {
          location = "/student";
        }
      }

      return location;
    } catch (e) {
      return null;
    }
  };

  // Shared by the password and passkey flows once the server has confirmed
  // the sign-in: stash the JWT and send the browser to its landing page.
  // Returns true if this was in fact a successful login, so callers can fall
  // through to their own error handling otherwise.
  const completeLogin = (payload, authorization) => {
    if (payload?.message !== "You are logged in.") return false;

    setToken(authorization);
    if (payload.remember_me_token) {
      setRememberMeToken(payload.remember_me_token);
    }
    window.location.replace(handleRedirectLocation(payload));
    return true;
  };

  // Runs the passkey ceremony and finishes the login. `mediation` is
  // "conditional" for the silent autofill attempt fired on mount, and unset
  // for the explicit "Sign in with a passkey" button -- both funnel through
  // here so a stray autofill match and a deliberate click can't race each
  // other into two logins.
  const runPasskeySignIn = async ({ signal, mediation } = {}) => {
    const options = await getPasskeyAuthenticationOptions();
    const credential = await getPasskeyAssertion(options, {
      signal,
      mediation,
    });
    if (!credential) return; // conditional UI resolved with nothing selected

    const { data: payload, authorization } = await authenticateWithPasskey(
      credential
    );

    if (completeLogin(payload, authorization)) return;

    setDbErrors(payload?.message || t("genericError"));
  };

  const handlePasskeySignIn = async () => {
    clearError();
    conditionalAbortRef.current?.abort();
    setPasskeyFlowActive(true);
    try {
      await runPasskeySignIn();
    } catch (e) {
      if (!(e instanceof PasskeyCancelledError)) {
        setDbErrors(t("genericError"));
      }
    }
  };

  // "I don't have my passkey anymore" -- drops out of the forced-passkey
  // flow into the normal password field, and tells the server (via
  // bypassPasskey on the next signIn call) to stop demanding a passkey for
  // this login even though one is still registered.
  const useAnotherWay = () => {
    conditionalAbortRef.current?.abort();
    setBypassPasskey(true);
    setPasskeyFlowActive(false);
    setShowPassword(true);
    clearError();
  };

  const handleLoggedInUser = () => {
    const token = getStoredToken();
    if (!token) return;

    // A dead token is no proof of a live session. Drop it and let the form
    // render instead of bouncing to a page the server will reject.
    if (isTokenExpired(token)) {
      removeToken();
      return;
    }

    try {
      const decoded = parseJwt(token);
      if (!decoded?.user) return;

      if (decoded.staff && decoded.default_school_id) {
        // Honor the school the staff last viewed (stored as a raw slug by the
        // school layout) so returning to the app root does not bounce a
        // multi-school user back to their default school. Fall back to the JWT
        // default when nothing was stored yet.
        let lastSchoolSlug = null;
        try {
          lastSchoolSlug = window.localStorage.getItem("current_school_slug");
        } catch (storageError) {
          lastSchoolSlug = null;
        }
        // Serialization accidents ("null", "undefined", "") must not become a
        // school slug; a stored slug that no longer resolves ends on the 404
        // page, whose home button drops it.
        if (
          !lastSchoolSlug ||
          lastSchoolSlug === "null" ||
          lastSchoolSlug === "undefined"
        ) {
          lastSchoolSlug = null;
        }
        const targetSlug = lastSchoolSlug || decoded.default_school_id;
        window.location.replace(`/v2/school/${encodeURIComponent(targetSlug)}`);
      } else if (decoded.guardian?.id) {
        window.location.replace("/guardian");
      } else if (decoded.student?.id) {
        window.location.replace("/student");
      }
    } catch (e) {
      removeToken();
    }
  };

  // Fired off the email field's arrow / Enter key, before any password is
  // known. Asks the server whether this email has passkey sign-in enabled;
  // if so we jump straight into the passkey ceremony (no password needed at
  // all), otherwise we reveal the password field for the normal flow.
  const checkMethod = async (email) => {
    try {
      const payload = await checkPasskey(email);
      if (payload?.message === "Passkey required." && passkeySupported) {
        handlePasskeySignIn();
      } else {
        setShowPassword(true);
      }
    } catch (e) {
      setDbErrors(t("genericError"));
    }
  };

  const onSubmit = async (data) => {
    setDbErrors(null);

    // First submit (email only, password field not shown yet): decide the
    // sign-in method instead of attempting a login.
    if (!showPassword) {
      checkMethod(data.email);
      return;
    }

    try {
      const {
        status,
        data: payload,
        authorization,
      } = await signIn({
        email: data.email,
        password: data.password,
        otpCode: useBackupCode ? undefined : data.otpCode,
        backupCode: useBackupCode ? data.backupCode : undefined,
        rememberMe: data.remember_me,
        locale: i18n.language,
        isPasskeySupported: passkeySupported,
        bypassPasskey,
      });

      if (status === PARTIAL_CONTENT_CODE) {
        if (
          payload?.message === "Passkey required." &&
          passkeySupported &&
          !bypassPasskey
        ) {
          handlePasskeySignIn();
        } else {
          setOtpDelivery(payload?.otp_delivery === "app" ? "app" : "email");
          setShowOtpScreen(true);
          return;
        }
      }

      if (completeLogin(payload, authorization)) return;

      setDbErrors(payload?.message || payload?.error || t("genericError"));
    } catch (e) {
      setDbErrors(t("genericError"));
    }
  };

  useEffect(() => {
    if (searchParams.get("force_logout") === "true") {
      removeToken();
      return;
    }
    // Landing here with a ?redirect= means the server (or an API 401) just
    // rejected this session as unauthenticated. A stale token left in storage
    // must not auto-bounce us back to that protected page, or login and the
    // page ping-pong forever. Drop it and show the form; after sign-in,
    // handleRedirectLocation honors the redirect target.
    if (searchParams.get("redirect")) {
      removeToken();
      return;
    }
    handleLoggedInUser();
  }, []);

  // Conditional UI: let the browser surface saved passkeys right in the
  // email field's autofill dropdown, no button needed. Silently no-ops
  // (browser support is inconsistent) and aborts cleanly if the user starts
  // a password submit or a manual passkey sign-in first.
  useEffect(() => {
    if (!passkeySupported) return undefined;

    let cancelled = false;
    const controller = new AbortController();
    conditionalAbortRef.current = controller;

    isConditionalMediationAvailable().then((available) => {
      if (!available || cancelled) return;
      runPasskeySignIn({
        signal: controller.signal,
        mediation: "conditional",
      }).catch((e) => {
        // This ceremony was never initiated by the user (it fires
        // automatically on mount to power autofill), so a failure here
        // -- cancelled, unsupported, no credential, whatever -- must stay
        // silent. Surfacing the generic error banner would greet the user
        // with "Something went wrong" before they've touched the form.
        if (!(e instanceof PasskeyCancelledError)) {
          // eslint-disable-next-line no-console
          console.error("Conditional passkey mediation failed:", e);
        }
      });
    });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, []);

  useEffect(() => {
    if (otpCode.length === NUMBER_OF_OTP_DIGITS) {
      setValue("otpCode", otpCode);
    }
  }, [otpCode]);

  if (showOtpScreen) {
    return (
      <Box sx={{ width: "100%", maxWidth: 420 }}>
        <Typography align="center" sx={{ mb: 2 }}>
          {/* eslint-disable-next-line no-nested-ternary */}
          {useBackupCode
            ? t("enterBackupCode")
            : otpDelivery === "app"
            ? t("enterMfaCodeApp")
            : t("enterMfaCode")}
        </Typography>

        <form onSubmit={handleSubmit(onSubmit)}>
          {useBackupCode ? (
            <LoginTextField
              required
              autoFocus
              type="text"
              id="backup-code-required"
              label={t("backupCode")}
              variant="filled"
              size="small"
              margin="dense"
              fullWidth
              {...register("backupCode", {
                required: true,
                onChange: clearError,
              })}
              slotProps={{
                htmlInput: {
                  autoCapitalize: "none",
                  autoCorrect: "off",
                  spellCheck: false,
                },
              }}
            />
          ) : (
            <OtpInput otpCode={otpCode} onChange={onChange} />
          )}
          <Stack sx={{ mt: 4, alignItems: "center", gap: 3 }}>
            <Button
              type="submit"
              variant="contained"
              sx={{
                px: 4,
                color: "white",
                textTransform: "none",
                boxShadow: "none",
                "&:hover": { boxShadow: "none" },
              }}
            >
              {t("continue")}
            </Button>
            {otpDelivery === "app" && (
              <Button
                type="button"
                variant="outlined"
                sx={{
                  color: "#ffffff",
                  fontSize: 14,
                  textTransform: "none",
                  borderColor: "#ffffff",
                }}
                onClick={() => {
                  setUseBackupCode((prev) => !prev);
                  clearError();
                }}
              >
                {useBackupCode ? t("useAuthenticatorCode") : t("useBackupCode")}
              </Button>
            )}
            {dbErrors && <Typography sx={dangerStyle}>{dbErrors}</Typography>}
          </Stack>
        </form>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", maxWidth: 348 }}>
      {isResetSuccess && (
        <Typography sx={successBannerStyle}>{t("passwordUpdated")}</Typography>
      )}
      <Typography align="center" sx={{ mb: 2 }}>
        {t("signInHeader")}
      </Typography>

      <form onSubmit={handleSubmit(onSubmit)}>
        <LoginTextField
          required
          autoFocus
          type="email"
          id="email-required"
          label={t("enterEmail")}
          variant="filled"
          size="small"
          margin="dense"
          fullWidth
          disabled={showPassword || passkeyFlowActive}
          {...register("email", { required: true, onChange: clearError })}
          slotProps={{
            htmlInput: {
              inputMode: "email",
              autoCapitalize: "none",
              autoCorrect: "off",
              spellCheck: false,
              // The "webauthn" token is what lets a supporting browser offer
              // saved passkeys in this field's autofill dropdown (conditional
              // UI, wired up in the useEffect above).
              autoComplete: "username webauthn",
            },
            input: {
              // Once the password field is showing, the email step is done --
              // its own submit button takes over.
              endAdornment: !showPassword && (
                <InputAdornment position="end">
                  <IconButton
                    type="submit"
                    aria-label={t("signInHeader")}
                    sx={{ p: 0, "&:hover": { background: "transparent" } }}
                  >
                    <ArrowCircleRight
                      color="success"
                      sx={{ height: 50, width: 40 }}
                    />
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />

        {showPassword && (
          <LoginTextField
            required
            autoFocus
            type="password"
            id="password-required"
            label={t("enterPassword")}
            size="small"
            variant="filled"
            margin="dense"
            fullWidth
            {...register("password", { required: true, onChange: clearError })}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      type="submit"
                      aria-label={t("signInHeader")}
                      sx={{ p: 0, "&:hover": { background: "transparent" } }}
                    >
                      <ArrowCircleRight
                        color="success"
                        sx={{ height: 50, width: 40 }}
                      />
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />
        )}

        {dbErrors && <Typography sx={dangerStyle}>{dbErrors}</Typography>}
      </form>

      {passkeyFlowActive && (
        <Box sx={{ mt: 3, textAlign: "center" }}>
          <Typography variant="body2" sx={{ color: "#ffffff", mb: 1 }}>
            {t("signingInWithPasskey")}
          </Typography>
          <Button
            type="button"
            variant="outlined"
            sx={{
              color: "#ffffff",
              textTransform: "none",
              borderColor: "#ffffff",
            }}
            onClick={useAnotherWay}
          >
            {t("signInAnotherWay")}
          </Button>
        </Box>
      )}

      {showPassword && (
        <Box sx={{ mt: { xs: 5, md: 8 }, textAlign: "center" }}>
          <Button
            component={RouterLink}
            variant="outlined"
            sx={{
              color: "#ffffff",
              textTransform: "none",
              borderColor: "#ffffff",
            }}
            to={`/forgot-password?locale=${i18n.language}`}
          >
            {t("forgotPassword")}
          </Button>
        </Box>
      )}
    </Box>
  );
}
